import { AuthRoleEnum } from '@codezo/graphql';
import { createContext, ReactNode, useContext, useState } from 'react';

/**
 * Simplified user type derived from backend user responses
 * This provides a cleaner interface for the auth context
 */
export interface AuthUser {
	id: string;
	email: string;
	role: AuthRoleEnum;
}

export interface AuthTokens {
	accessToken: string;
	refreshToken: string;
}

export interface AuthContextValue<TUser extends AuthUser> {
	isAuthenticated: boolean;
	user: TUser | null;
	setUser: (user: TUser | null) => void;
	setAccessToken: (accessToken: string | null) => void;
	token: string | null;
	logout: () => void;
}

const AuthContext = createContext<AuthContextValue<AuthUser> | undefined>(undefined);

export interface AuthProviderProps {
	children: ReactNode;
	loginUrl?: string;
}

/**
 * Simplified Auth provider that manages authentication state
 * - Access token is kept in memory only (never stored)
 * - Refresh token is stored in sessionStorage
 * - No auto-login or automatic refresh - handled by ReAuthenticateCallback
 */
export function AuthProvider({ children }: AuthProviderProps) {
	const [user, setUser] = useState<AuthUser | null>(null);
	const [accessToken, setAccessToken] = useState<string | null>(null);

	const logout = (): void => {
		// Redirect to the logout loading screen where the actual logout will be performed
		window.location.href = '/auth/logout';
	};

	const value: AuthContextValue<AuthUser> = {
		isAuthenticated: !!user && !!accessToken,
		user,
		setUser,
		token: accessToken || '',
		setAccessToken,
		logout,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Creates a typed useAuth hook for a specific user type
 * @returns A typed useAuth hook that provides type-safe access to authentication state
 */
export function createTypedUseAuth<TUser extends AuthUser>() {
	return function useTypedAuth(): AuthContextValue<TUser> {
		const context = useContext(AuthContext) as AuthContextValue<TUser> | undefined;
		if (!context) {
			throw new Error('useAuth must be used within AuthProvider');
		}
		return context;
	};
}
